import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardVariants = cva('border text-card-foreground shadow-none', {
    variants: {
        variant: {
            default: 'rounded-xl bg-card',
            dashed: 'rounded-xl border-dashed bg-muted/30',
            bare: 'rounded-xl border-0 bg-transparent',
            billing: 'rounded-[28px] border-border bg-card',
            danger: 'rounded-xl border-destructive/50 bg-card',
            accent: 'rounded-xl border-primary/20 bg-primary/5',
            home: 'rounded-2xl bg-card',
            startup: 'rounded-xl border-destructive/30 bg-card/95 shadow-none backdrop-blur',
            sidebar: 'rounded-xl border-sidebar-border bg-sidebar text-sidebar-foreground'
        }
    },
    defaultVariants: { variant: 'default' }
});

interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> { radius?: 'default' | 'large' }

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant = 'default', radius = 'default', ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            cardVariants({ variant }),
            radius === 'large' && 'rounded-2xl',
            className
        )}
        {...props}
    />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { flush?: boolean; density?: 'default' | 'compact' | 'comfortable' | 'flat'; spacing?: 'default' | 'relaxed' }
>(
    ({ className, flush = false, density = 'default', spacing = 'default', ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'flex flex-col',
                flush ? 'px-0 pt-0' : density === 'compact' ? 'space-y-0 p-6 pb-2' : density === 'comfortable' ? 'space-y-4 p-6 pb-4' : density === 'flat' ? 'space-y-0 p-6 pb-4' : spacing === 'relaxed' ? 'space-y-4 p-6' : 'space-y-1.5 p-6',
                className
            )}
            {...props}
        />
    )
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { tone?: 'default' | 'destructive' | 'sidebar'; size?: 'default' | 'base' | 'lg' | 'heading' }
>(
    ({ className, tone = 'default', size = 'default', ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'font-semibold leading-none tracking-tight',
                tone === 'destructive' && 'text-destructive',
                tone === 'sidebar' && 'text-sidebar-foreground',
                size === 'base' && 'text-base',
                (size === 'lg' || size === 'heading') && 'text-2xl',
                className
            )}
            {...props}
        />
    )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { tone?: 'default' | 'sidebar'; leading?: 'default' | 'relaxed' }>(
    ({ className, tone = 'default', leading = 'default', ...props }, ref) => (
        <div ref={ref} className={cn('text-sm', tone === 'sidebar' ? 'text-sidebar-foreground/70' : 'text-muted-foreground', leading === 'relaxed' && 'leading-6', className)} {...props} />
    )
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { flush?: boolean; table?: boolean; padding?: 'default' | 'all' | 'compact' | 'none'; spacing?: 'default' | 'comfortable' | 'generous' | 'relaxed'; layout?: 'default' | 'grid-tight' }
>(({ className, flush = false, table = false, padding = 'default', spacing = 'default', layout = 'default', ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            table ? 'border-t p-0' : flush ? 'px-0 pb-0' : padding === 'all' ? 'p-6' : padding === 'compact' ? 'p-5' : padding === 'none' ? 'p-0' : 'p-6 pt-0',
            spacing === 'comfortable' && 'space-y-4',
            spacing === 'generous' && 'space-y-6',
            spacing === 'relaxed' && 'space-y-6',
            layout === 'grid-tight' && 'grid gap-2',
            className
        )}
        {...props}
    />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { direction?: 'default' | 'column' }>(
    ({ className, direction = 'default', ...props }, ref) => (
        <div ref={ref} className={cn('flex items-center p-6 pt-0', direction === 'column' && 'flex-col gap-3', className)} {...props} />
    )
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
export type { CardProps };
